import { useEffect, useState, useMemo, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, Crown, ArrowLeft, Plus, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import EstimaBot from "../components/EstimaBot";
import { loadRazorpay } from "../lib/razorpay";
import { useAuth } from "../context/AuthContext";
import { useFeatureAccess } from "../context/FeatureAccessContext";
import { inrFull } from "../lib/format";
import useDocumentMeta, { META } from "../lib/useDocumentMeta";
import { formatTrialDuration } from "../lib/trialDuration";

const BASIC_FEATURES = [
  "Unlimited properties",
  "Portfolio tracker (current value, equity, appreciation)",
  "Sold-property ledger & realised-gains report",
  "Per-feature add-ons available · ₹99/mo",
];
const PRO_FEATURES = [
  "Everything in Basic",
  "AI Advisor (Claude Sonnet)",
  "All financial calculators (EMI, Rent vs Buy, XIRR, Loan Optimizer)",
  "Side-by-side property comparisons",
  "Loan rebalancer & part-payment nudges",
  "Tenants, Rent Comps, Projects, Will drafting, News",
  "PDF / Excel / CSV exports",
  "Priority support",
];

export default function Pricing() {
  useDocumentMeta(META.pricing);
  const { user, refresh: refreshUser } = useAuth();
  const { refresh: refreshAccess } = useFeatureAccess();
  const nav = useNavigate();
  const [pricing, setPricing] = useState(null);
  const [cycle, setCycle] = useState("monthly");
  const [pickedAddons, setPickedAddons] = useState(new Set()); // slugs (Basic only)
  const [busy, setBusy] = useState(false);
  const handlerFiredRef = useRef(false);

  useEffect(() => {
    api.get("/billing/pricing")
      .then(({ data }) => setPricing(data))
      .catch(() => toast.error("Couldn't load pricing."));
  }, []);

  const yearly = cycle === "yearly";

  // Add-ons split: Basic-included (free) vs purchasable, and the purchasable list keeps sub-features inline.
  const includedAddons = (pricing?.addons || []).filter((a) => a.default_in_basic);
  const purchasableAddons = (pricing?.addons || []).filter((a) => !a.default_in_basic);

  const basicTotal = useMemo(() => {
    if (!pricing) return 0;
    let total = yearly ? pricing.basic.yearly : pricing.basic.monthly;
    // Sum picked add-ons by resolving slug against addons or sub_features
    for (const slug of pickedAddons) {
      let item = pricing.addons.find((a) => a.slug === slug);
      if (!item) {
        for (const a of pricing.addons) {
          const sub = (a.sub_features || []).find((s) => s.slug === slug);
          if (sub) { item = sub; break; }
        }
      }
      if (item) total += yearly ? item.yearly : item.monthly;
    }
    return total;
  }, [pricing, yearly, pickedAddons]);

  const toggleAddon = (slug) => {
    setPickedAddons((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  const subscribe = async (target_plan) => {
    if (!user) {
      nav(`/register?next=/pricing`);
      return;
    }
    setBusy(true);
    try {
      const Razorpay = await loadRazorpay();
      const addons = target_plan === "basic"
        ? Array.from(pickedAddons).map((slug) => ({ slug, cycle }))
        : [];
      const { data: order } = await api.post("/billing/subscription/create-order", {
        target_plan, target_cycle: cycle, addons,
      });
      const opts = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "Estima",
        description: order.plan_label,
        order_id: order.order_id,
        prefill: { name: order.customer.name, email: order.customer.email },
        theme: { color: "#C85A32" },
        handler: async (resp) => {
          handlerFiredRef.current = true;
          try {
            await api.post("/billing/subscription/verify-payment", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success(`Welcome to ${order.plan_label}.`);
            await Promise.all([refreshUser(), refreshAccess()]);
            nav("/app");
          } catch (e) {
            const r = await reconcileRazorpayOrder("subscription", resp.razorpay_order_id);
            if (r.ok) {
              toast.success(`Welcome to ${order.plan_label}.`);
              await Promise.all([refreshUser(), refreshAccess()]);
              nav("/app");
            } else {
              toast.error(formatApiErrorDetail(e.response?.data?.detail));
            }
          } finally {
            setBusy(false);
          }
        },
        modal: {
          ondismiss: async () => {
            if (handlerFiredRef.current) { setBusy(false); return; }
            const r = await reconcileRazorpayOrder("subscription", order.order_id);
            setBusy(false);
            if (r.ok) {
              toast.success(`Payment received — welcome to ${order.plan_label}.`);
              await Promise.all([refreshUser(), refreshAccess()]);
              nav("/app");
            }
          },
        },
      };
      new Razorpay(opts).open();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!pricing) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground" data-testid="pricing-loading">Loading plans…</div>;
  }

  return (
    <div className="min-h-screen bg-background" data-testid="pricing-page">
      <header className="border-b hairline">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-8 w-8 border hairline flex items-center justify-center">
              <span className="font-serif text-xl leading-none">E</span>
            </div>
            <span className="font-serif text-2xl tracking-tight">Estima</span>
          </Link>
          <Link to={user ? "/app" : "/"} className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="pricing-back">
            <ArrowLeft size={14} /> {user ? "Back to app" : "Back home"}
          </Link>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-16">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="eyebrow mb-3">Pricing</div>
          <h1 className="font-serif text-5xl md:text-6xl mb-5">Start with {formatTrialDuration(pricing.trial_days || 180)} free.</h1>
          <p className="text-muted-foreground text-lg">
            Two tiers, both with a {formatTrialDuration(pricing.trial_days || 180)} free trial on signup (Basic features).
            Pick Basic for portfolio tracking + add-ons. Pick Pro for everything.
          </p>
        </div>

        <div className="flex justify-center mb-10">
          <div className="inline-flex border hairline" role="tablist">
            <button role="tab" aria-selected={!yearly} onClick={() => setCycle("monthly")}
              className={`px-5 py-2 text-sm transition ${!yearly ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}
              data-testid="pricing-cycle-monthly">Monthly</button>
            <button role="tab" aria-selected={yearly} onClick={() => setCycle("yearly")}
              className={`px-5 py-2 text-sm transition ${yearly ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}
              data-testid="pricing-cycle-yearly">Yearly · save 17%</button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {/* Basic */}
          <div className="card-flat p-8 flex flex-col" data-testid="plan-card-basic">
            <div className="font-serif text-3xl mb-2">Basic</div>
            <div className="text-xs text-muted-foreground mb-4">Portfolio tracker for landlords.</div>
            <div className="num-metric text-5xl mb-1">{inrFull(yearly ? pricing.basic.yearly : pricing.basic.monthly)}</div>
            <div className="text-xs text-muted-foreground mb-6">{yearly ? "per year, billed once" : "per month"}</div>
            <ul className="space-y-3 mb-6 flex-1">
              {BASIC_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-sm">
                  <Check size={16} weight="bold" className="text-[hsl(var(--secondary))] mt-0.5 shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
              {includedAddons.map((a) => (
                <li key={a.slug} className="flex items-start gap-2.5 text-sm" data-testid={`pricing-included-${a.slug}`}>
                  <Check size={16} weight="bold" className="text-[hsl(var(--secondary))] mt-0.5 shrink-0" />
                  <span>
                    {a.name}{" "}
                    <span className="eyebrow text-[hsl(var(--secondary))] ml-1">Included</span>
                  </span>
                </li>
              ))}
            </ul>

            <div className="border-t hairline pt-4 mb-4">
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Bundle add-ons (optional)</div>
              <div className="space-y-1.5 max-h-72 overflow-auto pr-1" data-testid="pricing-basic-addons">
                {purchasableAddons.map((a) => {
                  const checked = pickedAddons.has(a.slug);
                  const price = yearly ? a.yearly : a.monthly;
                  const subs = a.sub_features || [];
                  return (
                    <div key={a.slug}>
                      <label
                        className={`flex items-center justify-between gap-3 px-3 py-2 text-sm cursor-pointer border hairline transition ${checked ? "bg-[hsl(var(--muted))] border-[hsl(var(--secondary))]" : "hover:bg-[hsl(var(--muted))]"}`}
                        data-testid={`pricing-addon-toggle-${a.slug}`}>
                        <span className="flex items-center gap-2 min-w-0">
                          <input type="checkbox" checked={checked} onChange={() => toggleAddon(a.slug)} className="shrink-0" data-testid={`pricing-addon-check-${a.slug}`} />
                          <span className="truncate">{a.name}</span>
                          {subs.length > 0 && (
                            <span className="text-[10px] text-muted-foreground">({subs.length} sub-features)</span>
                          )}
                        </span>
                        <span className="text-xs text-muted-foreground shrink-0">+ {inrFull(price)}</span>
                      </label>
                      {/* Nested sub-features */}
                      {subs.length > 0 && (
                        <div className="ml-6 mt-1 space-y-1" data-testid={`pricing-addon-subs-${a.slug}`}>
                          {subs.map((sub) => {
                            const subChecked = pickedAddons.has(sub.slug);
                            const subPrice = yearly ? sub.yearly : sub.monthly;
                            return (
                              <label
                                key={sub.slug}
                                className={`flex items-center justify-between gap-3 px-3 py-1.5 text-xs cursor-pointer border-l hairline transition ${subChecked ? "bg-[hsl(var(--muted))] border-l-[hsl(var(--secondary))]" : "hover:bg-[hsl(var(--muted))]"}`}
                                data-testid={`pricing-subfeature-toggle-${sub.slug}`}>
                                <span className="flex items-center gap-2 min-w-0">
                                  <input
                                    type="checkbox"
                                    checked={subChecked}
                                    disabled={checked}
                                    onChange={() => toggleAddon(sub.slug)}
                                    className="shrink-0"
                                    data-testid={`pricing-subfeature-check-${sub.slug}`}
                                  />
                                  <span className="truncate text-muted-foreground">└ {sub.name}</span>
                                </span>
                                <span className="text-[10px] text-muted-foreground shrink-0">
                                  {checked ? <span className="line-through">+ {inrFull(subPrice)}</span> : <>+ {inrFull(subPrice)}</>}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
                {purchasableAddons.length === 0 && (
                  <div className="text-xs text-muted-foreground py-2">All add-ons are already included with Basic.</div>
                )}
              </div>
              {pickedAddons.size > 0 && (
                <div className="mt-3 flex items-center justify-between text-xs">
                  <button onClick={() => setPickedAddons(new Set())} className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="pricing-addons-clear">
                    <X size={11} /> Clear add-ons
                  </button>
                  <span className="num-metric text-base">Total: {inrFull(basicTotal)}</span>
                </div>
              )}
            </div>

            <button onClick={() => subscribe("basic")} disabled={busy}
              className="btn-ghost text-sm py-3 text-center disabled:opacity-60" data-testid="plan-basic-cta">
              {!user
                ? "Start free trial"
                : pickedAddons.size > 0
                  ? `Subscribe to Basic + ${pickedAddons.size} add-on${pickedAddons.size > 1 ? "s" : ""} · ${inrFull(basicTotal)}`
                  : "Subscribe to Basic"}
            </button>
          </div>

          {/* Pro */}
          <div className="card-flat p-8 flex flex-col border-[hsl(var(--secondary))]" data-testid="plan-card-pro">
            <div className="eyebrow text-[hsl(var(--secondary))] mb-2 inline-flex items-center gap-1">
              <Crown size={12} weight="duotone" /> Most popular
            </div>
            <div className="font-serif text-3xl mb-2">Pro</div>
            <div className="text-xs text-muted-foreground mb-4">Everything unlocked.</div>
            <div className="num-metric text-5xl mb-1">{inrFull(yearly ? pricing.pro.yearly : pricing.pro.monthly)}</div>
            <div className="text-xs text-muted-foreground mb-6">{yearly ? "per year, billed once" : "per month"}</div>
            <ul className="space-y-3 mb-8 flex-1">
              {PRO_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-sm">
                  <Check size={16} weight="bold" className="text-[hsl(var(--secondary))] mt-0.5 shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <button onClick={() => subscribe("pro")} disabled={busy}
              className="btn-primary text-sm py-3 text-center disabled:opacity-60" data-testid="plan-pro-cta">
              {user ? "Subscribe to Pro" : "Start free trial"}
            </button>
          </div>
        </div>

        <div className="mt-16">
          <div className="text-center mb-6">
            <div className="eyebrow mb-2">Add-ons reference</div>
            <h2 className="font-serif text-3xl">All add-on prices</h2>
            <p className="text-sm text-muted-foreground mt-2">
              Bundle them with Basic above, or add anytime later from your profile.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 max-w-4xl mx-auto">
            {pricing.addons.map((a) => (
              <div key={a.slug} className="border hairline p-4" data-testid={`pricing-addon-${a.slug}`}>
                <div className="text-sm font-medium mb-1 inline-flex items-center gap-2">
                  <Plus size={12} weight="bold" className="text-[hsl(var(--secondary))]" /> {a.name}
                  {a.default_in_basic && (
                    <span className="eyebrow text-[hsl(var(--secondary))] ml-1">Free in Basic</span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  {a.default_in_basic ? "Included free with Basic plan" : `${inrFull(a.monthly)}/mo · ${inrFull(a.yearly)}/yr`}
                </div>
                {(a.sub_features || []).length > 0 && (
                  <div className="mt-2 pt-2 border-t hairline space-y-0.5">
                    {a.sub_features.map((s) => (
                      <div key={s.slug} className="flex justify-between text-[11px] text-muted-foreground">
                        <span>└ {s.name}</span>
                        <span>{inrFull(s.monthly)}/mo</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-16 text-center text-xs text-muted-foreground max-w-2xl mx-auto">
          Payments handled by Razorpay. {formatTrialDuration(pricing.trial_days || 180)} free trial on every signup with Basic features — no card needed.
          Once on a Pro plan, downgrade is locked until the period ends.
        </div>
      </div>
      <EstimaBot page="pricing" />
    </div>
  );
}
